// #FLEET_LIBRARY_V1 (2026-10-29) — Bill: "Just a fleet Library to store docs." Vendor / OEM paperwork Fleet keeps
// on file that isn't tied to one unit (per-unit invoices, titles, inspections stay on the unit's Files in the Master):
// telematics connection guides, warranty letters, manuals, quotes and contracts, vendor contact sheets.
//
// Folders:  OEM / Telematics — Owner / Fleet manager only (vendor guides marked CONFIDENTIAL, account numbers)
//           Warranty · Manuals & Service · Quotes & Contracts · Other — anyone who can open Fleet
// Files:    private 'equip-files' bucket under library/<folder>/ — same bucket, RLS and signed links as the
//           Equipment Master unit files (#FLEET_EQUIPFILES_V1): Fleet view opens, Fleet edit adds / removes.
// Catalog:  app_storage 'fleet-library' = [{ id, name, cat, year, file, path, size, type, hash, addedBy, addedAt }]
// RULE: no passwords, API keys or logins in here — those live in Vercel only. The library holds documents.
import React, { useEffect, useMemo, useRef, useState } from 'react';

export const FLEET_LIB_KEY = 'fleet-library';
const BUCKET = 'equip-files';
const OK_EXT = /\.(pdf|docx?|xlsx?|png|jpe?g|txt)$/i;
const P = { card:'#171B1F', cardHi:'#1F252B', line:'#2C343C', text:'#F4F6F7', muted:'#95A1A9', accent:'#FF6A00', ink:'#0A0C0E', green:'#34D399', red:'#F87171', blue:'#5B8DEF', purple:'#A78BFA', gold:'#FDE047' };

export const FLEET_LIB_CATS = [
  { id:'oem',      label:'OEM / Telematics',    color:P.accent, mgr:true, hint:'Factory telematics connection guides, API spec sheets, account numbers, vendor contacts. Owner / Fleet manager only. No passwords or keys here, those stay in Vercel.' },
  { id:'warranty', label:'Warranty',            color:P.green,  hint:'Warranty letters, coverage end dates, extended-warranty contracts.' },
  { id:'manuals',  label:'Manuals & Service',   color:P.blue,   hint:'Operator, parts and service manuals, service bulletins.' },
  { id:'quotes',   label:'Quotes & Contracts',  color:P.gold,   hint:'Vendor quotes, rental / service agreements, subscription renewals.' },
  { id:'other',    label:'Other',               color:P.purple, hint:'Anything else Fleet wants on file.' },
];

const MIME = { pdf:'application/pdf', doc:'application/msword', docx:'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls:'application/vnd.ms-excel', xlsx:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', png:'image/png', jpg:'image/jpeg', jpeg:'image/jpeg', txt:'text/plain' };
const extOf = (n) => ((String(n || '').match(/\.([a-z0-9]{2,5})$/i) || [])[1] || 'pdf').toLowerCase();
const kb = (b) => !b ? '' : b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
const cleanName = (fn) => String(fn || '').replace(/^.*[\\/]/, '').replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[_+]/g, ' ').replace(/\s{2,}/g, ' ').trim() || 'Document';
const yearOf = (fn) => { const m = String(fn || '').match(/\b(20[0-4]\d)\b/); return m ? m[1] : ''; };
async function sha(file) {
  try { const h = await crypto.subtle.digest('SHA-256', await file.arrayBuffer()); return Array.from(new Uint8Array(h)).map(x => x.toString(16).padStart(2, '0')).join('').slice(0, 40); }
  catch { return ''; }
}

export default function FleetLibrary({ sg, ss, supaUrl, supaKey, getToken, userUpn, editable, manager }) {
  const me = String(userUpn || '').trim().toLowerCase();
  const cats = manager ? FLEET_LIB_CATS : FLEET_LIB_CATS.filter(c => !c.mgr);
  const [cat, setCat] = useState(cats[0].id);
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ name:'', year:'', cat:'' });
  const [delId, setDelId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const filesRef = useRef(null);
  const listRef = useRef(null);
  const signCache = useRef({});
  useEffect(() => { listRef.current = list; }, [list]);
  useEffect(() => { (async () => { try { const v = await sg(FLEET_LIB_KEY); setList(Array.isArray(v) ? v : []); } catch { setList([]); } })(); }, [sg]);

  const hdr = async (extra) => {
    const tok = getToken ? await getToken() : null;
    if (!tok) throw new Error('Your sign-in has expired — reload DOPE and try again.');
    return { apikey: supaKey, Authorization: `Bearer ${tok}`, ...(extra || {}) };
  };
  const fresh = async () => { try { const v = await sg(FLEET_LIB_KEY); if (Array.isArray(v)) return v; } catch {} return listRef.current || []; };
  const saveList = async (next) => { listRef.current = next; setList(next); try { await ss(FLEET_LIB_KEY, next); } catch (e) { setMsg({ ok:false, text:'Could not save the list: ' + (e.message || e) }); } };
  const sign = async (path, download) => {
    const k = `${path}|${download || ''}`; const hit = signCache.current[k];
    if (hit && hit.exp > Date.now() + 60000) return hit.url;
    try {
      const res = await fetch(`${supaUrl}/storage/v1/object/sign/${BUCKET}/${path}`, { method:'POST', headers: await hdr({ 'Content-Type':'application/json' }), body: JSON.stringify({ expiresIn: 3600 }) });
      if (!res.ok) return '';
      const d = await res.json();
      let url = d && d.signedURL ? `${supaUrl}/storage/v1${d.signedURL}` : '';
      if (url && download) url += `&download=${encodeURIComponent(download)}`;
      if (url) signCache.current[k] = { url, exp: Date.now() + 3600 * 1000 };
      return url;
    } catch { return ''; }
  };
  // tab opened inside the tap so phones don't block it as a pop-up, then pointed at the signed link
  const openFile = async (x, download) => {
    const w = download ? null : window.open('', '_blank');
    const u = await sign(x.path, download ? `${x.name}.${x.type || 'pdf'}`.replace(/[\\/:*?"<>|#%]/g, '-') : '');
    if (!u) { if (w) w.close(); setMsg({ ok:false, text:'Could not open that file — your sign-in may have expired. Reload (Ctrl+Shift+R) and try again.' }); return; }
    if (w) w.location.href = u; else window.location.href = u;
  };

  const counts = useMemo(() => { const c = {}; (list || []).forEach(x => { c[x.cat] = (c[x.cat] || 0) + 1; }); return c; }, [list]);
  const shown = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (list || []).filter(x => x.cat === cat)
      .filter(x => !words.length || words.every(w => `${x.name} ${x.file} ${x.year || ''}`.toLowerCase().includes(w)))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric:true, sensitivity:'base' }));
  }, [list, cat, q]);
  const C = FLEET_LIB_CATS.find(c => c.id === cat) || cats[0];

  const load = async (files) => {
    if (!editable) return;
    const good = files.filter(f => OK_EXT.test(f.name));
    if (!good.length) { setMsg({ ok:false, text:'Nothing to load — PDF, Word, Excel, text or image files only.' }); return; }
    const big = good.filter(f => f.size > 25 * 1048576);
    setMsg(null);
    let cur = (await fresh()).slice();
    const seen = new Set(cur.map(x => x.hash).filter(Boolean));
    let added = 0, dup = 0; const failed = big.map(f => `${f.name} (over 25 MB)`);
    const todo = good.filter(f => f.size <= 25 * 1048576);
    for (let i = 0; i < todo.length; i++) {
      const f = todo[i];
      setBusy(`Loading ${i + 1} of ${todo.length} — ${f.name}`);
      const h = await sha(f);
      if (h && seen.has(h)) { dup++; continue; }
      const ext = extOf(f.name);
      const path = `library/${cat}/${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
      const type = (f.type && f.type !== 'application/octet-stream') ? f.type : (MIME[ext] || 'application/pdf');
      try {
        const res = await fetch(`${supaUrl}/storage/v1/object/${BUCKET}/${path}`, { method:'POST', headers: await hdr({ 'Content-Type': type, 'x-upsert':'true' }), body: f });
        if (!res.ok) { failed.push(`${f.name} (${res.status})`); if (failed.length >= 3 && !added) break; continue; }
      } catch (e) { failed.push(`${f.name} (${e.message || e})`); continue; }
      cur.push({ id:`flib-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: cleanName(f.name), cat, year: yearOf(f.name), file: f.name, path, size: f.size, type: ext, hash: h, addedBy: me, addedAt: new Date().toISOString() });
      if (h) seen.add(h); added++;
    }
    if (added) await saveList(cur);
    setBusy('');
    setMsg({ ok: !failed.length, text: `Added ${added} to ${C.label}` + (dup ? ` · ${dup} duplicate${dup === 1 ? '' : 's'} skipped` : '') + (failed.length ? ` · ${failed.length} failed: ${failed.slice(0, 3).join('; ')}` : '') });
  };
  const saveEdit = async (id) => {
    const nm = edit.name.trim(); setEditId(null); if (!nm) return;
    await saveList((await fresh()).map(x => x.id === id ? { ...x, name: nm, year: edit.year.trim(), cat: edit.cat || x.cat } : x));
  };
  const remove = async (x) => {
    setDelId(null);
    await saveList((await fresh()).filter(y => y.id !== x.id));
    try { await fetch(`${supaUrl}/storage/v1/object/${BUCKET}/${x.path}`, { method:'DELETE', headers: await hdr() }); } catch {}
  };

  const inp = { background:P.card, border:`1px solid ${P.line}`, color:P.text, borderRadius:8, padding:'9px 11px', fontSize:14, outline:'none', boxSizing:'border-box', width:'100%' };
  const btn = (c, solid) => ({ background: solid ? c : 'transparent', color: solid ? P.ink : c, border:`1px solid ${c}`, borderRadius:8, padding:'6px 11px', fontSize:12, fontWeight:800, cursor:'pointer', whiteSpace:'nowrap', textDecoration:'none', display:'inline-block' });
  const tag = (c) => ({ fontSize:9.5, fontWeight:900, letterSpacing:.4, color:c, border:`1px solid ${c}88`, borderRadius:5, padding:'1px 5px', textTransform:'uppercase' });
  const row = { display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderBottom:`1px solid ${P.line}`, flexWrap:'wrap' };

  return (
    <div style={{ color:P.text }}
      onDragOver={e => { if (editable) { e.preventDefault(); setDragOver(true); } }} onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); if (editable) load(Array.from(e.dataTransfer.files || [])); }}>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
        {cats.map(c => (
          <button key={c.id} onClick={() => { setCat(c.id); setQ(''); setEditId(null); setDelId(null); }}
            style={{ ...btn(c.color, cat === c.id), padding:'9px 14px', fontSize:13 }}>
            {c.label} <span style={{ opacity:.75, fontWeight:700 }}>· {counts[c.id] || 0}</span>
          </button>
        ))}
      </div>
      <div style={{ fontSize:12.5, color:P.muted, marginBottom:10 }}>{C.hint}</div>

      {editable && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', background:P.cardHi, border:`1px ${dragOver ? 'dashed' : 'solid'} ${dragOver ? P.accent : P.line}`, borderRadius:10, padding:'10px 12px', marginBottom:12 }}>
          <span style={{ fontSize:11, fontWeight:900, letterSpacing:.6, textTransform:'uppercase', color:P.accent }}>Fleet · manage</span>
          <button disabled={!!busy} onClick={() => filesRef.current && filesRef.current.click()} style={btn(C.color, true)}>+ Add to {C.label}</button>
          <span style={{ fontSize:11.5, color:P.muted }}>or drag files onto this page · PDF / Word / Excel / images · 25 MB max · duplicates skipped</span>
          <input ref={filesRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt" onChange={e => { const fs = Array.from(e.target.files || []); e.target.value = ''; load(fs); }} style={{ display:'none' }} />
        </div>
      )}
      {busy && <div style={{ fontSize:12.5, color:P.accent, fontWeight:800, marginBottom:10 }}>{busy}</div>}
      {msg && <div onClick={() => setMsg(null)} style={{ fontSize:12.5, fontWeight:700, marginBottom:10, padding:'8px 11px', borderRadius:8, cursor:'pointer', color: msg.ok ? P.green : P.red, background:`${msg.ok ? P.green : P.red}14`, border:`1px solid ${msg.ok ? P.green : P.red}55` }}>{msg.text}</div>}

      <input value={q} onChange={e => setQ(e.target.value)} placeholder={`🔎 Search ${C.label}…`} style={{ ...inp, marginBottom:10 }} />

      <div style={{ border:`1px solid ${P.line}`, borderRadius:12, overflow:'hidden' }}>
        {list == null ? <div style={{ padding:18, color:P.muted }}>Loading…</div>
          : shown.map(x => (
            <div key={x.id} style={row}>
              {editId === x.id ? (
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', flex:1, alignItems:'center' }}>
                  <input autoFocus value={edit.name} onChange={e => setEdit(s => ({ ...s, name:e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') saveEdit(x.id); if (e.key === 'Escape') setEditId(null); }} style={{ ...inp, flex:'2 1 200px', width:'auto', padding:'6px 8px', fontSize:13 }} />
                  <input value={edit.year} placeholder="Year" onChange={e => setEdit(s => ({ ...s, year:e.target.value.replace(/[^0-9]/g, '').slice(0, 4) }))} style={{ ...inp, width:80, padding:'6px 8px', fontSize:13 }} />
                  <select value={edit.cat} onChange={e => setEdit(s => ({ ...s, cat:e.target.value }))} style={{ ...inp, width:'auto', padding:'6px 8px', fontSize:13 }}>{cats.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}</select>
                  <button onClick={() => saveEdit(x.id)} style={btn(P.green, true)}>Save</button>
                  <button onClick={() => setEditId(null)} style={btn(P.muted)}>Cancel</button>
                </div>
              ) : (
                <>
                  <div style={{ flex:'1 1 240px', minWidth:0 }}>
                    <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                      <span style={{ fontWeight:800, fontSize:13.5 }}>{x.name}</span>
                      {x.year && <span style={tag(C.color)}>{x.year}</span>}
                      {x.type && x.type !== 'pdf' && <span style={tag(P.muted)}>{x.type}</span>}
                    </div>
                    <div style={{ fontSize:10.5, color:P.muted, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{x.file} · {kb(x.size)}{x.addedAt ? ` · added ${String(x.addedAt).slice(0, 10)}` : ''}</div>
                  </div>
                  <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                    <a href="#view" onClick={e => { e.preventDefault(); openFile(x); }} style={btn(P.blue)}>View / Print</a>
                    <a href="#download" onClick={e => { e.preventDefault(); openFile(x, true); }} style={btn(P.muted)}>Download</a>
                    {editable && (delId === x.id
                      ? <><button onClick={() => remove(x)} style={btn(P.red, true)}>Remove</button><button onClick={() => setDelId(null)} style={btn(P.muted)}>Keep</button></>
                      : <>
                          <span title="Rename / year / move" onClick={() => { setEditId(x.id); setEdit({ name:x.name, year:x.year || '', cat:x.cat }); }} style={{ cursor:'pointer', color:P.muted, padding:'4px 5px' }}>✎</span>
                          <span title="Remove from library" onClick={() => setDelId(x.id)} style={{ cursor:'pointer', color:P.muted, padding:'4px 5px' }}>🗑</span>
                        </>)}
                  </div>
                </>
              )}
            </div>
          ))}
        {list != null && !shown.length && (
          <div style={{ padding:'22px 16px', textAlign:'center', color:P.muted }}>
            {q ? <>Nothing in {C.label} matches "{q}".</> : editable ? <>Nothing in {C.label} yet — click <b style={{ color:C.color }}>+ Add to {C.label}</b> or drag files here.</> : <>Nothing in {C.label} yet.</>}
          </div>
        )}
      </div>
    </div>
  );
}
